/**
 * Vivus configuration file types.
 *
 * The vivus.json file allows repository-level configuration for:
 * - Alert suppression (ignore known false positives)
 * - Default assignee for issues
 * - Target namespace/cluster declarations
 */

import type {
  ClusterName,
  ControllerName,
  NamespaceName,
} from "../k8s/ids";

/**
 * Alert types that can be suppressed via vivus.json
 */
export const AlertTypes = {
  HighRestarts: "highRestarts",
  MissingLimits: "missingLimits",
  NodeResources: "nodeResources",
  Overprovisioning: "overprovisioning",
  PodStatus: "podStatus",
} as const;

export type AlertTypeKey = keyof typeof AlertTypes;
export type AlertType = (typeof AlertTypes)[AlertTypeKey];

export const alertTypes = Object.values(AlertTypes);

/**
 * Suppression reason for a specific alert type
 */
export type SuppressionReason = {
  reason: string;
};

/**
 * Target selector for suppression rules.
 * At least one field must be specified.
 */
export type VivusRuleTarget = {
  /** Cluster name to match (exact match) */
  cluster?: ClusterName;
  /** Controller name to match (exact match, e.g., "web-app", "easyocr") */
  controller?: ControllerName;
  /** Namespace to match (exact match) */
  namespace?: NamespaceName;
};

/**
 * Suppression rule configuration
 */
export type VivusRule = {
  /** Suppression settings per alert type */
  suppress: Partial<Record<AlertType, SuppressionReason>>;
  /** Target selector - which resources this rule applies to */
  target: VivusRuleTarget;
};

/**
 * Target declaration for namespace/cluster binding
 */
export type VivusTarget = {
  /** Cluster name where this repo's workloads run */
  cluster: ClusterName;
  /** Namespace where this repo's workloads run */
  namespace: NamespaceName;
};

/**
 * Manual workload-to-repo mapping override.
 * Declared in a repo's vivus.json to claim ownership of a specific workload.
 */
export type RepoMapping = {
  /** Namespace where the workload runs */
  namespace: NamespaceName;
  /** Workload (Deployment/StatefulSet/DaemonSet) name to map to this repo */
  workload: ControllerName;
};

/**
 * Root vivus.json configuration structure
 */
export type VivusConfig = {
  /** Default GitHub username to assign issues to */
  defaultAssignee?: string;
  /** Manual workload-to-repo mapping overrides */
  repoMappings?: RepoMapping[];
  /** Suppression rules */
  rules?: VivusRule[];
  /** Target namespace/cluster declarations */
  targets?: VivusTarget[];
  /** Config version (currently only "1") */
  version: "1";
};

/**
 * Parameters for checking if an alert should be suppressed
 */
export type SuppressionCheck = {
  /** Alert type being checked */
  alertType: AlertType;
  /** Cluster name where the alert originated */
  cluster: ClusterName;
  /** Controller name (optional, e.g., "web-app", "easyocr") */
  controller?: ControllerName;
  /** Namespace where the alert originated */
  namespace: NamespaceName;
};

/**
 * Result of a suppression check
 */
export type SuppressionResult = {
  /** The rule that matched (if suppressed) - uses unknown for flexibility with Effect Schema */
  matchedRule?: unknown;
  /** Reason for suppression (from the matching rule) */
  reason?: string;
  /** Source of the matching config */
  source?: "fallback" | "repo";
  /** Whether the alert should be suppressed */
  suppressed: boolean;
};
